import { and, assign, setup } from 'xstate';
import { createActorContext } from '@xstate/react';

export interface ShapeEditorMachineContext {
    selectedPointIndex: number;
    points: Cesium.Cartesian3[];
    nextPoint?: Cesium.Cartesian3;
    extendDirection: 'start' | 'end' | null;
    mode: 'LineString' | 'Polygon';
    snappingFrameCount: number;
    snappingShapes: Cesium.Cartesian3[][];
    isSnappingEnabled: boolean;
}

export type MachineEvent =
    | { type: 'createClickMap'; position?: Cesium.Cartesian3 }
    | { type: 'createClickEndIcon' }
    | {
          type: 'createMouseMove';
          position?: Cesium.Cartesian3;
          snappingShapes: Cesium.Cartesian3[][];
      }
    | { type: 'createUndoLastPoint' }
    | { type: 'editClickPoint'; index?: number }
    | { type: 'editMouseDownPoint'; index: number }
    | { type: 'editMouseDownHalfHandle'; index: number; position: Cesium.Cartesian3 }
    | {
          type: 'editDraggingMouseMove';
          position?: Cesium.Cartesian3;
          snappingShapes: Cesium.Cartesian3[][];
      }
    | { type: 'editDraggingMouseUp'; position: Cesium.Cartesian3 }
    | { type: 'editDeleteSelectedPoint' }
    | { type: 'editEnterCreateModeFromEndpoint' }
    | { type: 'changeIsSnappingEnabled'; isEnabled: boolean }
    | { type: 'enterCreateModeFromEndpoint'; index: number };

export const shapeEditorMachine = setup({
    types: {
        context: {} as ShapeEditorMachineContext,
        events: {} as MachineEvent,
        input: {} as {
            points: Cesium.Cartesian3[];
            mode: 'LineString' | 'Polygon';
            isSnappingEnabled: boolean;
        },
    },
    actions: {
        triggerChange: () => {
            // This gets overwritten to save the changes
        },
        createAddNewPoint: assign({
            points: ({ context, event }) => {
                if (event.type !== 'createClickMap' || event.position == null)
                    return context.points;

                const { points, extendDirection } = context;

                if (extendDirection === 'start') {
                    // prepend
                    return [event.position, ...points];
                } else if (extendDirection === 'end') {
                    // append
                    return [...points, event.position];
                } else {
                    // fallback to append
                    return [...points, event.position];
                }
            },
        }),
        createUndoLastPoint: assign({
            // remove the last point from the point array
            points: ({ context }) => {
                const { points, extendDirection } = context;

                // no-op
                if (points.length === 0) return points;
                // remove last remaining point
                if (points.length === 1) return [];

                return extendDirection === 'start' ? points.slice(1) : points.slice(0, -1);
            },
        }),
        createFinalise: assign({
            points: ({ context }) => {
                const { points, mode } = context;
                // Close the polygon
                const [firstPoint] = points;
                if (mode === 'Polygon' && firstPoint != null) {
                    return [...points, firstPoint];
                }
                return points;
            },
            extendDirection: null,
        }),
        createUpdateNextPointPosition: assign({
            snappingShapes: ({ context, event }) => {
                return event.type !== 'createMouseMove'
                    ? context.snappingShapes
                    : event.snappingShapes;
            },
            nextPoint: ({ context, event }) => {
                return event.type !== 'createMouseMove' || event.position == null
                    ? context.nextPoint
                    : event.position;
            },
        }),
        createStop: assign({
            nextPoint: undefined,
            extendDirection: null,
        }),
        editChangeSelection: assign({
            selectedPointIndex: ({ context, event }) => {
                if (
                    event.type === 'editClickPoint' ||
                    event.type === 'editMouseDownPoint' ||
                    event.type === 'editMouseDownHalfHandle'
                ) {
                    return event.index ?? -1;
                }
                if (event.type === 'editDeleteSelectedPoint') {
                    return -1;
                }
                return context.selectedPointIndex;
            },
        }),
        editRemovePoint: assign({
            points: ({ event, context }) => {
                const { selectedPointIndex, points, mode } = context;
                // if no point is selected, delete does nothing
                if (event.type === 'editDeleteSelectedPoint' && selectedPointIndex !== -1) {
                    if (mode === 'Polygon' && points.length > 4) {
                        // if the first or last point is selected, handle that weirdness
                        if (selectedPointIndex === 0 || selectedPointIndex === points.length - 1) {
                            const [, newInitialPoint] = points;
                            if (newInitialPoint != null) {
                                return points.slice(1, -1).concat(newInitialPoint);
                            }
                        }
                        // just delete the point
                        else {
                            return points.filter((_, index) => index !== selectedPointIndex);
                        }
                    }
                    if (mode === 'LineString' && points.length > 2) {
                        return points.filter((_, index) => index !== selectedPointIndex);
                    }
                }
                return points;
            },
        }),
        editMoveDraggedPoint: assign({
            snappingShapes: ({ context, event }) => {
                return event.type !== 'editDraggingMouseMove'
                    ? context.snappingShapes
                    : event.snappingShapes;
            },
            points: ({ event, context }) => {
                const { selectedPointIndex, points, mode } = context;

                if (
                    event.type !== 'editDraggingMouseMove' ||
                    selectedPointIndex === -1 ||
                    event.position == null
                ) {
                    return points;
                }

                const updatedPoints = [...points];
                // Move both the first and last point if in Polygon mode and editing the first/last point
                if (
                    mode === 'Polygon' &&
                    (selectedPointIndex === 0 || selectedPointIndex === points.length - 1)
                ) {
                    updatedPoints[0] = event.position;
                    updatedPoints[updatedPoints.length - 1] = event.position;
                } else {
                    updatedPoints[selectedPointIndex] = event.position;
                }

                return updatedPoints;
            },
        }),
        editAddNewPoint: assign({
            points: ({ event, context }) => {
                if (event.type === 'editMouseDownHalfHandle') {
                    const updatedPoints = [...context.points];
                    updatedPoints.splice(event.index, 0, event.position);
                    return updatedPoints;
                }
                return context.points;
            },
        }),
        incrementSnappingFrame: assign({
            snappingFrameCount: ({ context }) => context.snappingFrameCount + 1,
        }),
        setIsSnappingEnabled: assign({
            isSnappingEnabled: ({ context, event }) => {
                return event.type === 'changeIsSnappingEnabled'
                    ? event.isEnabled
                    : context.isSnappingEnabled;
            },
        }),
        setExtendDirection: assign({
            extendDirection: ({ context }) => {
                return context.selectedPointIndex === 0 ? 'start' : 'end';
            },
            selectedPointIndex: -1,
            nextPoint: undefined,
        }),
    },
    guards: {
        noData: function ({ context }) {
            return context.points.length === 0;
        },
        someData: function ({ context }) {
            return context.points.length !== 0;
        },
        canReenterCreateMode: ({ context }) => {
            return context.mode === 'LineString';
        },
    },
}).createMachine({
    context: ({ input }) => ({
        points: input.points,
        mode: input.mode,
        selectedPointIndex: -1,
        snappingFrameCount: 0,
        snappingShapes: [],
        isSnappingEnabled: input.isSnappingEnabled,
        extendDirection: null,
    }),
    id: 'ShapeEditor',
    initial: 'FormOpen',
    states: {
        FormOpen: {
            always: [
                {
                    target: 'Creating',
                    guard: {
                        type: 'noData',
                    },
                },
                {
                    target: 'Editing',
                    guard: {
                        type: 'someData',
                    },
                },
            ],
        },
        Creating: {
            initial: 'createWaitForInput',
            exit: {
                type: 'createStop',
            },
            states: {
                createWaitForInput: {
                    on: {
                        createClickMap: {
                            target: 'createWaitForInput',
                            actions: [
                                {
                                    type: 'createAddNewPoint',
                                },
                                {
                                    type: 'triggerChange',
                                },
                            ],
                        },
                        createUndoLastPoint: {
                            target: 'createWaitForInput',
                            actions: [
                                {
                                    type: 'createUndoLastPoint',
                                },
                                {
                                    type: 'triggerChange',
                                },
                            ],
                        },
                        createClickEndIcon: {
                            target: '#ShapeEditor.Editing',
                            actions: {
                                type: 'createFinalise',
                            },
                        },
                        createMouseMove: {
                            target: 'createWaitForInput',
                            actions: [
                                {
                                    type: 'incrementSnappingFrame',
                                },
                                {
                                    type: 'createUpdateNextPointPosition',
                                },
                            ],
                        },
                        changeIsSnappingEnabled: {
                            target: 'createWaitForInput',
                            actions: {
                                type: 'setIsSnappingEnabled',
                            },
                        },
                    },
                },
            },
        },
        Editing: {
            initial: 'editWaitForInput',
            states: {
                editWaitForInput: {
                    on: {
                        editClickPoint: [
                            {
                                target: 'editWaitForInput',
                                actions: [
                                    {
                                        type: 'editChangeSelection',
                                    },
                                ],
                            },
                        ],
                        editEnterCreateModeFromEndpoint: {
                            guard: 'canReenterCreateMode',
                            target: '#ShapeEditor.Creating',
                            actions: [
                                {
                                    type: 'setExtendDirection',
                                },
                            ],
                        },
                        editMouseDownPoint: {
                            target: 'editDraggingPoint',
                            actions: [
                                {
                                    type: 'editChangeSelection',
                                },
                            ],
                        },
                        editMouseDownHalfHandle: {
                            target: 'editDraggingPoint',
                            actions: [
                                {
                                    type: 'editAddNewPoint',
                                },
                                {
                                    type: 'editChangeSelection',
                                },
                                {
                                    type: 'triggerChange',
                                },
                            ],
                        },
                        editDeleteSelectedPoint: {
                            target: 'editWaitForInput',
                            actions: [
                                {
                                    type: 'editRemovePoint',
                                },
                                {
                                    type: 'editChangeSelection',
                                },
                                {
                                    type: 'triggerChange',
                                },
                            ],
                        },
                        changeIsSnappingEnabled: {
                            target: 'editWaitForInput',
                            actions: {
                                type: 'setIsSnappingEnabled',
                            },
                        },
                    },
                    entry: [
                        {
                            type: 'triggerChange',
                        },
                    ],
                },
                editDraggingPoint: {
                    on: {
                        editDraggingMouseMove: {
                            target: 'editDraggingPoint',
                            actions: [
                                {
                                    type: 'incrementSnappingFrame',
                                },
                                {
                                    type: 'editMoveDraggedPoint',
                                },
                            ],
                        },
                        editDraggingMouseUp: {
                            target: 'editWaitForInput',
                        },
                    },
                },
            },
        },
    },
});

export const ShapeEditorActorContext = createActorContext(shapeEditorMachine);
